// banks-info-report.component.ts
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { debounceTime, finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ItfgDataTableColumn } from '@app/core/components/data-table/types/data-table.column';
import { Client } from '@app/core/types/client';
import { IrisService } from '@app/core/services/iris.service';
import { NotificationService } from '@app/core/services/notification.service';
import {
  IrisReportResponse,
  IrisTransaction,
  IrisCreditDebitIndicator,
  IrisSummaryFiltersDTO,
} from '@app/core/types/iris-report';
import { BankAccountView } from './bank-info-view.type';
import moment from 'moment';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { ItfgDateAdapter } from '@app/core/adapters/date-adapter.service';
import { DateFormat } from '@app/core/types/date-format';


const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'itfg-banks-info-report',
  templateUrl: './banks-info-report.component.html',
  styleUrls: ['./banks-info-report.component.scss'],
  providers: [
    { provide: DateAdapter, useClass: ItfgDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        subscriptSizing: 'dynamic',
      },
    },
  ],
  standalone: false,

})
export class BanksInfoReportComponent implements OnInit, OnChanges, OnDestroy {
  @Input() client: Client;
  dateFormat = DateFormat.DATE;

  _unsubscribe: Subject<void> = new Subject<void>();
  _filterChange: Subject<void> = new Subject<void>();
  creditDebitIndicators: typeof IrisCreditDebitIndicator =
    IrisCreditDebitIndicator;

  reportData: IrisReportResponse | null = null;
  bankAccountViews: BankAccountView[] = [];
  isLoading = false;
  hasData = false;

  transactionColumns: ItfgDataTableColumn[] = [];

  // Pagination per account
  currentPage: { [iban: string]: number } = {};
  pageSize = 20;

  // Filters
  directionFilter: string[] = [];
  amountFromFilter: number | null = null;
  amountToFilter: number | null = null;
  descriptionFilter = '';
  dateFromFilter: Date | null = null;
  dateToFilter: Date | null = null;

  directionOptions = [
    {
      value: IrisCreditDebitIndicator.CREDIT,
      label: 'bankInfo.filters.incoming',
    },
    {
      value: IrisCreditDebitIndicator.DEBIT,
      label: 'bankInfo.filters.outgoing',
    },
  ];

  constructor(
    private irisService: IrisService,
    private translateService: TranslateService,
    private notification: NotificationService
  ) { }

  ngOnInit(): void {
    this.initColumns();
    this._filterChange
      .pipe(debounceTime(400), takeUntil(this._unsubscribe))
      .subscribe(() => this.loadReport());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.client && this.client?.civilId) {
      this.loadReport();
    }
  }

  onFilterChange(): void {
    this._filterChange.next();
  }

  buildFilters(): IrisSummaryFiltersDTO {
    return {
      dateFrom: this.dateFromFilter
        ? moment(this.dateFromFilter).format('YYYY-MM-DD')
        : undefined,
      dateTo: this.dateToFilter
        ? moment(this.dateToFilter).format('YYYY-MM-DD')
        : undefined,
      description: this.descriptionFilter || undefined,
      amountFrom: this.amountFromFilter ?? undefined,
      amountTo: this.amountToFilter ?? undefined,
    };
  }

  initColumns(): void {
    this.translateService
      .get([
        'bankInfo.columns.date',
        'bankInfo.columns.description',
        'bankInfo.columns.counterparty',
        'bankInfo.columns.counterpartyIban',
        'bankInfo.columns.amount',
        'bankInfo.columns.direction',
      ])
      .subscribe((t) => {
        this.transactionColumns = [
          {
            name: 'bookingDate',
            label: t['bankInfo.columns.date'],
            width: 110,
          },
          {
            name: 'direction',
            label: t['bankInfo.columns.direction'],
            width: 60,
          },
          {
            name: 'counterpartyName',
            label: t['bankInfo.columns.counterparty'],
            width: { min: 160 },
          },
          {
            name: 'counterpartyIban',
            label: t['bankInfo.columns.counterpartyIban'],
            width: { min: 180 },
          },
          {
            name: 'amount',
            label: t['bankInfo.columns.amount'],
            width: 140,
            numeric: true,
          },
          {
            name: 'description',
            label: t['bankInfo.columns.description'],
            width: { min: 200 },
          },
        ];
      });
  }

  loadReport(): void {
    if (!this.client?.civilId) return;
    this.isLoading = true;

    this.irisService
      .getBankSummary(this.client.civilId, this.buildFilters())
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (data) => {
          this.reportData = data;
          this.processReportData();
          this.hasData = this.bankAccountViews.length > 0;
        },
        error: () => {
          this.hasData = false;
          this.notification.showLocalizedErrorMessage({
            notificationText: 'bankInfo.errors.fetchFailed',
          });
        },
      });
  }

  refreshReport(): void {
    this.irisService.updateSummary(this.client?.civilId)
      .pipe(takeUntil(this._unsubscribe))
      .subscribe({
        next: () => {
          this.loadReport()
        },
        error: () => {
          this.hasData = false;
          this.notification.showLocalizedErrorMessage({
            notificationText: 'bankInfo.errors.fetchFailed',
          });
        },
      });
  }

  processReportData(): void {
    if (!this.reportData?.irisUsers) {
      this.bankAccountViews = [];
      return;
    }

    const views: BankAccountView[] = [];
    const clientBrandCode = this.client?.brand?.code || '';

    for (const user of this.reportData.irisUsers) {
      const accounts = Array.isArray(user.bankAccounts)
        ? user.bankAccounts
        : [user.bankAccounts];
      for (const account of accounts) {
        if (!account) continue;
        views.push(new BankAccountView(account, user.brand, clientBrandCode));
        this.currentPage[account.iban] = 0;
      }
    }

    views.sort((a, b) => b.turnover - a.turnover);

    if (views.length > 0) {
      views[0].expanded = true;
    }

    this.bankAccountViews = views;
  }

  toggleExpand(view: BankAccountView): void {
    view.expanded = !view.expanded;
  }

  getPagedTransactions(view: BankAccountView): IrisTransaction[] {
    const page = this.currentPage[view.account.iban] || 0;
    const start = page * this.pageSize;
    return view.transactions.slice(start, start + this.pageSize);
  }

  getTotalFilteredCount(view: BankAccountView): number {
    return view.transactions.length;
  }

  getTotalPages(view: BankAccountView): number {
    return Math.ceil(this.getTotalFilteredCount(view) / this.pageSize);
  }

  pageChanged(view: BankAccountView, event: any): void {
    this.currentPage[view.account.iban] = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  getCounterpartyName(txn: IrisTransaction): string {
    return txn.creditDebitIndicator === IrisCreditDebitIndicator.CREDIT
      ? txn.debtorName || '-'
      : txn.creditorName || '-';
  }

  getCounterpartyIban(txn: IrisTransaction): string {
    return txn.creditDebitIndicator === IrisCreditDebitIndicator.CREDIT
      ? txn.debtorAccount?.iban || '-'
      : txn.creditorAccount?.iban || '-';
  }

  clearFilters(): void {
    this.directionFilter = [];
    this.amountFromFilter = null;
    this.amountToFilter = null;
    this.descriptionFilter = '';
    this.dateFromFilter = null;
    this.dateToFilter = null;
    this.onFilterChange();
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
